import { ATIVO_MERCADO, PESSOA_TIPO } from '@investirapri/financa.js/lib/enums';
import { validators } from 'investira.sdk';

function countNewNotifications(pData, pNotifications) {
  const xPrevNotificacoesSize = Object.values(pNotifications).length;
  const xNotificacoesSize = Object.values(pData).length;
  const xNewNotificationsAmount = Math.abs(xPrevNotificacoesSize - xNotificacoesSize);
  return xNewNotificationsAmount;
}

function getPath(pPathname = '', size = 1) {
  const xResult = pPathname.split('/')[size];
  return xResult;
}

function getRoute(pPathname = '', size = 2) {
  let xSize = size - 1;
  if (size <= 1) {
    xSize = 1;
  }
  let xResult = pPathname.split('/');
  //const xPathSize = xResult.length;
  xResult = xResult.slice(0, xResult - xSize);
  xResult = xResult.join('/');

  return xResult;
}

function getPessoaTipoById(pId) {
  const xResultado = [];

  if (validators.isEmpty(pId)) {
    return xResultado;
  }

  for (const pKey in PESSOA_TIPO) {
    if (PESSOA_TIPO.hasOwnProperty(pKey)) {
      const xTipo = PESSOA_TIPO[pKey];

      if ((pId & xTipo.id) === xTipo.id) {
        xResultado.push(xTipo);
      }
    }
  }

  return xResultado;
}

function debounce(pFunc, pDelay = 1000) {
  let timer;

  return function () {
    if (timer) {
      clearTimeout(timer);
    }

    timer = setTimeout(() => {
      pFunc.apply(this, arguments);
    }, pDelay);
  };
}

function getAtivoDescricao(pAtivo, pRegra) {
  if (!pAtivo) {
    console.error('É necessário informar o ativo!');
    return null;
  }

  const { mercado, ativo_seuid, ativo, ticker } = pAtivo;

  switch (mercado) {
    case ATIVO_MERCADO.TPU.id:
      return `${ativo} - ${ativo_seuid}`;
    case ATIVO_MERCADO.TPR.id:
      return ativo_seuid;
    case ATIVO_MERCADO.FND.id:
      return formatTituloFundo(ativo);
    default:
      return pRegra || ticker || ativo_seuid || ativo;
  }
}

function sortAndFilterValues(pValues = [], pSortKey = 'descricao', pRemoveValues = []) {
  const xValues = Object.values(pValues).filter(xValue => {
    return !pRemoveValues.includes(xValue.id);
  });

  return Object.values(xValues).sort((a, b) => {
    if (a[pSortKey] < b[pSortKey]) {
      return -1;
    }
    if (a[pSortKey] > b[pSortKey]) {
      return 1;
    }

    return 0;
  });
}

/**
 * Formata o título de fundos de investimento para Title Case, exceto para palavras específicas
 * que devem permanecer sempre em UPPERCASE ou lowercase conforme dicionários internos.
 * Frases conhecidas são substituídas por abreviações antes da formatação.
 *
 * Exemplos:
 * - "BB ESPELHO MULTIMERCADO SPX NIMITZ PRIVATE FIC DE FIF RESPONSABILIDADE LIMITADA"
 *    -> "BB Espelho Multimercado SPX Nimitz Private FIC de FIF RL"
 *
 * @param {string} pTitulo Título do fundo a ser tratado.
 * @returns {string} Título formatado.
 */
function formatTituloFundo(pTitulo) {
  if (!pTitulo || typeof pTitulo !== 'string') {
    return '';
  }

  // Lista de palavras que SEMPRE devem ficar em UPPERCASE
  const wUppercase = [
    'BB',
    'SPX',
    'FIC',
    'FIA',
    'FIM',
    'FIM CP',
    'FIF',
    'FI',
    'FIM IE',
    'FII',
    'IE',
    'CP',
    'RF',
    'ETF',
    'RL',
    'XP',
    'BTG',
    'BNDES',
    'IEE',
    'UBS',
    'IPCA',
    'CI',
    '2A',
    'BNP',
    'CIC',
    'II',
    'III',
    'IV',
    'VI',
    'VII',
    'FIX',
    'DI',
    'CFIN',
    'TPF',
    'FIDC',
    'RT',
    'PB',
    'PGBL',
    'VGBL',
    'CDB',
    'SLQ',
    'AA',
    'HSI',
    'LC',
    'RE',
    'ER',
    'PA',
    'SE',
    'CGR',
    'TRX',
    'CRA',
    'XI'
  ];

  // Lista de palavras que SEMPRE devem ficar em lowercase
  const wLowercase = [
    'de',
    'do',
    'da',
    'dos',
    'das',
    'e',
    'a',
    'o',
    'os',
    'as',
    'em',
    'por',
    'para',
    'com',
    'sem',
    'na',
    'no',
    'nas',
    'nos'
  ];

  // Frases que devem ser substituídas por abreviações (case-insensitive)
  const wAbreviacoes = {
    'RESPONSABILIDADE LIMITADA': '', // Ocultar para ativos, manter para empresas
    ' - RESPONSABILIDADE LIMITADA': '',
    'FIXA-': 'FIXA'
  };

  // Substitui frases por abreviações antes da formatação (frases mais longas primeiro)
  let xTitulo = pTitulo.trim();
  const xFrasesAbreviacao = Object.keys(wAbreviacoes).sort((a, b) => b.length - a.length);

  for (const xFrase of xFrasesAbreviacao) {
    const xRegex = new RegExp(xFrase.replace(/\s+/g, '\\s+'), 'gi');
    xTitulo = xTitulo.replace(xRegex, wAbreviacoes[xFrase]);
  }

  // Constrói um set para buscas mais eficientes (case-insensitive)
  const wUpperSet = new Set(wUppercase.map(w => w.toUpperCase()));
  const wLowerSet = new Set(wLowercase.map(w => w.toLowerCase()));

  // Divide considerando espaços, mas também preservando siglas com pontos ou hífens
  const xPalavras = xTitulo.split(/\s+/);

  const xResult = xPalavras.map((xPalavra, i) => {
    const xClean = xPalavra.replace(/[.,;:!?()-]/g, ''); // Palavra sem pontuação para consulta
    // Checagem case-insensitive
    if (wUpperSet.has(xClean.toUpperCase())) {
      return xClean.toUpperCase();
    }
    if (wLowerSet.has(xClean.toLowerCase())) {
      return xClean.toLowerCase();
    }

    // Default: primeira letra maiúscula, resto minúsculas
    // Mantém a pontuação original, se houver
    const xFirst = xPalavra.charAt(0);
    const xRest = xPalavra.slice(1);

    if (xClean.length === 0) return xPalavra;
    return xFirst.toUpperCase() + xRest.toLowerCase();
  });

  return xResult.join(' ');
}

const helpers = {
  countNewNotifications,
  getPath,
  getRoute,
  getPessoaTipoById,
  debounce,
  getAtivoDescricao,
  sortAndFilterValues,
  formatTituloFundo
};

export default helpers;
